import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { FormEvent, useMemo, useState } from 'react';
import {
  useAuthUserRestaurant,
  useCreateSupplierMutation,
  ValidationError,
} from '@durma-soft/gros-sdk';
import { SupplierWithEmails } from '@durma-soft/gros-sdk/dist/types/suppliers';

import { Modal } from '@/components/shared/modal';
import { Button } from '@/components/shared/shadcn-ui/button';
import { DialogFooter } from '@/components/shared/shadcn-ui/dialog';
import {
  SupplierFormData,
  SupplierFormFields,
  isSupplierFormValid,
} from '@/components/restaurant/suppliers/supplier-form-fields';

const initialFormData: SupplierFormData = { name: '', emails: [''] };

interface CreateSupplierModalProps {
  isOpen: boolean;
  onClose: () => unknown;
  onCreated?: (supplier: SupplierWithEmails) => unknown;
}

export const CreateSupplierModal = ({
  isOpen,
  onClose,
  onCreated,
}: CreateSupplierModalProps) => {
  const { t } = useTranslation();
  const restaurant = useAuthUserRestaurant();

  const [formData, setFormData] = useState<SupplierFormData>(initialFormData);

  const createSupplier = useCreateSupplierMutation();

  const isValid = useMemo(() => isSupplierFormValid(formData), [formData]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) return;
    try {
      const supplier = await createSupplier.mutateAsync({
        restaurant_id: restaurant.id,
        name: formData.name,
        emails: formData.emails,
      });
      onClose();
      setFormData(initialFormData);
      toast.success(t('suppliers.create-success-msg'));
      onCreated?.(supplier);
    } catch (error) {
      if (error instanceof ValidationError) {
        return toast.error(error.message);
      }
      toast.error(t('suppliers.create-error-msg'));
    }
  };

  return (
    <Modal
      isModalOpen={isOpen}
      closeModal={onClose}
      title={t('suppliers.create')}
      description={t('suppliers.create-details')}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <SupplierFormFields formData={formData} setFormData={setFormData} />
        <DialogFooter className="mt-2">
          <Button
            type="submit"
            disabled={!isValid}
            isLoading={createSupplier.isPending}
          >
            {t('suppliers.create')}
          </Button>
        </DialogFooter>
      </form>
    </Modal>
  );
};
